import { KeyboardEvent, MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import { ThemeProvider } from '@mui/material/styles';
import { ENTER_KEY } from 'common/constants/general';
import { CustomScalarSpreadsheet } from 'components/CustomScalarSpreadsheet';
import useWorkbook from 'components/ScalarSpreadsheet/utilities/useWorkbook';
import { markWorkboardSortHeaderForFocus } from 'dashboard409a/components/WorkboardSpreadsheet/WorkboardSortHeader';
import useWorkboardModelsSheet from 'dashboard409a/hooks/useWorkboardModelsSheet';
import { MODELS_COLUMN_IDS } from 'dashboard409a/pages/Workboard/common/constants';
import lightTheme from 'theme';
import { WorkboardModelsSpreadsheetProps } from './WorkboardModelsSpreadsheet.props';

const SKELETON_ROW_COUNT = 12;
const DEFAULT_COLUMN_WIDTH = 150;
// Matches the old ModelsTable virtualizer estimateSize.
const VIRTUAL_ROW_HEIGHT = 50;
const ROW_OVERSCAN_COUNT = 8;
const COLUMN_OVERSCAN_COUNT = 2;

const INTERACTIVE_COLUMN_IDS = new Set<string>([
  MODELS_COLUMN_IDS.MODEL_STATUS,
  MODELS_COLUMN_IDS.NOTES,
]);

const WorkboardModelsSkeleton = ({ columnCount }: { columnCount: number }) => (
  <Box sx={{ backgroundColor: '#fff', flex: 1, minHeight: 0, overflow: 'hidden', p: 1 }}>
    <Skeleton variant="rectangular" height={36} sx={{ mb: 0.5, borderRadius: 0.5 }} />
    {Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
      <Box key={i} sx={{ display: 'flex', gap: 0.5, mb: 0.5 }}>
        {Array.from({ length: Math.max(columnCount, 1) }).map((__, j) => (
          <Skeleton
            key={j}
            variant="rectangular"
            height={34}
            sx={{ flex: 1, borderRadius: 0.5, opacity: 1 - i * 0.06 }}
          />
        ))}
      </Box>
    ))}
  </Box>
);

const WorkboardModelsSpreadsheet = ({
  models,
  visibleColumnIds,
  isLoading = false,
  isNoteLoading = false,
  columnWidths,
  sortCriteria,
  onRequestSort,
  onColumnResize,
  onRowClick,
  onStatusUpdate,
  onNoteSave,
}: WorkboardModelsSpreadsheetProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [viewportSize, setViewportSize] = useState({ height: 0, width: 0 });

  useEffect(() => {
    const element = containerRef.current;
    if (!element || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const updateSize = () => {
      setViewportSize({
        height: Math.floor(element.clientHeight),
        width: Math.floor(element.clientWidth),
      });
    };

    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const columnWidthsForSpreadsheet = useMemo(() => {
    if (!columnWidths || Object.keys(columnWidths).length === 0) {
      return undefined;
    }
    // Keys must match columnTypes (column ids) so resize lookup works.
    return visibleColumnIds.reduce<{ column: string; width: number }[]>((acc, id) => {
      const width = columnWidths[id];
      if (width !== undefined) {
        acc.push({ column: id, width });
      }
      return acc;
    }, []);
  }, [columnWidths, visibleColumnIds]);

  const virtualColumnWidths = useMemo(
    () => visibleColumnIds.map(id => columnWidths?.[id] ?? DEFAULT_COLUMN_WIDTH),
    [columnWidths, visibleColumnIds]
  );

  const virtualization = useMemo(() => {
    if (viewportSize.height <= 0 || viewportSize.width <= 0 || virtualColumnWidths.length === 0) {
      return undefined;
    }
    return {
      rowHeight: VIRTUAL_ROW_HEIGHT,
      rowOverscanCount: ROW_OVERSCAN_COUNT,
      height: viewportSize.height,
      width: viewportSize.width,
      columnWidths: virtualColumnWidths,
      columnOverscanCount: COLUMN_OVERSCAN_COUNT,
    };
  }, [viewportSize.height, viewportSize.width, virtualColumnWidths]);

  const modelById = useMemo(
    () => new Map(models.map(model => [String(model.id), model])),
    [models]
  );

  const handleSheetClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement | null;
      const cell = target?.closest('td') as HTMLTableCellElement | null;
      if (!cell) {
        return;
      }
      if (cell.classList.contains('table-header') || cell.classList.contains('legend')) {
        return;
      }

      const columnRef = cell.getAttribute('data-column-ref') ?? '';
      if (INTERACTIVE_COLUMN_IDS.has(columnRef) || cell.classList.contains('workboard-interactive-cell')) {
        return;
      }

      const recordId = cell.getAttribute('data-alias');
      if (!recordId) {
        return;
      }

      const model = modelById.get(recordId);
      if (!model) {
        return;
      }

      onRowClick(model, event);
    },
    [onRowClick, modelById]
  );

  // Datasheet keeps focus on `.data-grid-container` and eats Tab/Enter for cell nav,
  // so sort headers rarely receive key events. Activate from the selected header cell.
  const handleSortKeyDownCapture = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== ENTER_KEY && event.key !== ' ') {
        return;
      }
      if (!onRequestSort) {
        return;
      }

      const target = event.target as HTMLElement | null;
      if (!target) {
        return;
      }

      const grid = target.closest('.data-grid-container');
      if (!grid) {
        return;
      }

      let sortButton = target.closest('[data-workboard-sort-header="true"]') as HTMLElement | null;
      let headerCell = sortButton?.closest('td') as HTMLTableCellElement | null;

      if (!sortButton) {
        headerCell = grid.querySelector('td.cell.selected.table-header') as HTMLTableCellElement | null;
        sortButton = headerCell?.querySelector('[data-workboard-sort-header="true"]') as HTMLElement | null;
      }

      if (!sortButton || !headerCell) {
        return;
      }

      const columnId = headerCell.getAttribute('data-column-ref');
      if (!columnId) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.nativeEvent.stopImmediatePropagation();

      const title = sortButton.getAttribute('data-workboard-sort-title');
      if (title) {
        markWorkboardSortHeaderForFocus(title);
      }

      onRequestSort(event as unknown as MouseEvent<unknown>, columnId, event.shiftKey);
    },
    [onRequestSort]
  );

  const { spreadsheet } = useWorkboardModelsSheet({
    models,
    visibleColumnIds,
    isNoteLoading,
    sortCriteria,
    onRequestSort,
    onStatusUpdate,
    onNoteSave,
  });

  const spreadsheets = useMemo(() => [spreadsheet], [spreadsheet]);

  const { cells, data, onChange, workbook } = useWorkbook(spreadsheets);

  const showSkeleton = isLoading || !data || !cells || !data.workboard_models;
  const sheet = data?.workboard_models;
  const isViewportReady = viewportSize.height > 0 && viewportSize.width > 0;
  const showSheet = Boolean(sheet && !showSkeleton && isViewportReady && virtualization);

  return (
    <ThemeProvider theme={lightTheme}>
      <Box
        ref={containerRef}
        onClick={showSheet ? handleSheetClick : undefined}
        onKeyDownCapture={showSheet ? handleSortKeyDownCapture : undefined}
        sx={{
          backgroundColor: '#fff',
          flex: 1,
          minHeight: 0,
          // Datasheet owns scrolling when virtualized.
          overflow: 'hidden',
          '& .data-grid': {
            width: '100%',
            tableLayout: 'fixed',
          },
          '& .data-grid td.workboard-row-click-cell': {
            cursor: 'pointer',
          },
          // Lift default 175px max-width on notes only — do not use !important so
          // saved/resized inline column widths still win.
          '& .data-grid td.workboard-notes-cell': {
            maxWidth: 'none',
            padding: 0,
            textAlign: 'left',
            overflow: 'hidden',
          },
          '& .data-grid td.workboard-empty-message-cell': {
            color: 'text.secondary',
            fontStyle: 'italic',
            textAlign: 'left',
          },
          '& #spreadsheet-table__title-columns': {
            display: 'none',
          },
          '& #spreadsheet-table__content-columns': {
            maxWidth: '100% !important',
            width: '100%',
          },
        }}>
        {!showSheet || !sheet || !virtualization ? (
          <WorkboardModelsSkeleton columnCount={visibleColumnIds.length} />
        ) : (
          <CustomScalarSpreadsheet
            isLoading={false}
            data={sheet.data ?? []}
            onChange={onChange}
            sheet={sheet as any}
            columns={sheet.columns}
            rowConfig={sheet.rowConfig}
            cells={cells!.workboard_models}
            workbook={workbook}
            showTitlesColumn={false}
            showTotalColumn={false}
            displayRowIndicator
            displayColumnIndicator
            alwaysDisplayLegend={false}
            columnWidths={columnWidthsForSpreadsheet}
            columnTypes={visibleColumnIds}
            onColumnResize={onColumnResize}
            virtualization={virtualization}
            disablePerfectScrollbar
            isReadOnly
          />
        )}
      </Box>
    </ThemeProvider>
  );
};

export default WorkboardModelsSpreadsheet;
